import type { intlShape } from 'react-intl';
import type ICustomCategoriesMessages from './containers/CustomCategories';
import type { IMetaProductListingMessages } from './containers/MetaProductListing';
import type { IRestrictionListingMessages } from './containers/RestrictionListing';
import type ISyscoSelfServiceMessages from './containers/SyscoSelfService';

export interface IMessages {
	containers: IContainerMessages;
	components: IComponentMessages;
	validation: IGenericErrorMessages;
	common: ICommonMessages;
	breadCrumbs: IBreadCrumbMessages;
	copyrightMessages: ICopyrightMessages;
	privacyMessages: IPrivacyMessages;
}

export interface ICopyrightMessages {
	copyright: string;
	confidentialityNotice: string;
}

export interface IPrivacyMessages {
	loginNoticeTerms: string;
	loginNoticePrivacy: string;
	termsOfUse: string;
	privacyPolicy: string;
	cookiePolicy: string;
	californiaCollectionNotice: string;
	privacyChoices: string;
}

export interface IBreadCrumbMessages {
	masterOrderGuides: string;
	newMasterOrderGuide: string;
	editMasterOrderGuide: string;
	massImport: string;
	sequenceCharts: string;
}

export interface IContainerMessages {
	home: IHomeMessages;
	metaProductWorkflow: IMetaProductWorkflowMessages;
	restrictionsWorkflow: IRestrictionsWorkflowMessages;
	restrictionListing: IRestrictionListingMessages;
	restrictionLocationAssignment: IRestrictionLocationAssignmentMessages;
	locationAssignment: ILocationAssignmentMessages;
	distributionCenterAssignment: IDistributionCenterAssignmentMessages;
	login: ILoginContainerMessages;
	singleSignOn: ISingleSignOnMessages;
	browserUnsupported: IBrowserUnsupportedContainerMessages;
	customers: ICustomersMessages;
	admin: IAdminContainerMessages;
	reports: ICustomerReportMessages;
	blueprintManagement: IBlueprintManagementMessages;
	search: ISearchContainerMessages;
	activity: IActivityMessages;
	alerts: IAlertsMessages;
	metaProductListing: IMetaProductListingMessages;
	orderGuideDownload: IOrderGuideDownloadMessages;
	stockMatrix: IStockMatrixMessages;
	MassCustomerItemNumberImport: IMassCustomerItemNumberImportMessages;
	syscoSelfService: ISyscoSelfServiceMessages;
	customCategories: ICustomCategoriesMessages;
}

export interface IComponentMessages {
	addBySupcModal: IAddBySupcModalMessages;
	dataTable: IDataTableMessages;
	navigationBar: INavigationBarMessages;
	proprietary: IProprietary;
	branding: IBranding;
	userProfileCard: IUserProfileCardMessages;
	productDetailsSlideout: IProductDetailsSlideoutMessages;
	nutritionLabel: INutritionLabelMessages;
	coverageMessages: ICoverageMessages;
	rebatePill: IRebatePill;
	specIndicator: ISpecIndicatorMessages;
	suggestionDetail: ISuggestionDetailMessages;
	priceAttributes: IPriceAttributes;
}

export interface IDataTableMessages {
	searchTable: string;
}

export interface ICoverageMessages {
	oneAdditional: string;
	noAdditionalCoverage: string;
	multipleAdditional: string;
}

export interface IGenericErrorMessages {
	errorOnPageLoad: string;
	nameCannotBeEmpty: string;
	generic: string;
}

export interface ICommonMessages {
	save: string;
	apply: string;
	cancel: string;
	edit: string;
	delete: string;
	roles: IRoleMessages;
	central: string;
	distributionCenter: string;
	distributionCenters: string;
	location: string;
	locations: string;
}

export interface IStatus {
	pending: string;
	inProgress: string;
	completed: string;
	error: string;
}

export interface IRoleMessages {
	userAdmin: string;
	readOnly: string;
	externalUser: string;
	categoryAdmin: string;
	ogmAI: string;
	ogmAITrial: string;
}

export interface ICustomerReportMessages {
	allCustomers: string;
	customerReports: string;
	requestReports: string;
	generatedReports: string;
	toasts: IReportRequestToasts;
	reportTable: ICustomerReportTableMessages;
	reportRequestTable: IReportRequestTableMessages;
}

export interface IReportRequestToasts {
	successTitle: string;
	successBody: string;
	errorTitle: string;
	errorBody: string;
}

export interface IReportRequestTableMessages {
	reportTypeHeader: string;
	statusHeader: string;
	requestedByHeader: string;
	actionsHeader: string;
	requestAction: string;
	reportRequestCurrentlyProcessing: string;
	otherReportCurrentlyPending: string;
	status: IStatus;
}

export interface ICustomerReportNames {
	chartSyncStatus: string;
	discrepancyDetails: string;
	actionableChartErrors: string;
	stockMatrix: string;
	massImport: string;
	phaseOutAutoAddEnabled: string;
	phaseOutAutoAddDisabled: string;
	massLocationAdd: string;
	orderGuide: string;
	aramarkDiscrepancyDetails: string;
	aramarkFileStatus: string;
	aramarkProprietaryItem: string;
	aramarkOpcoSupcChangesSummary: string;
	aramarkFullCatalogSummary: string;
	discontinuedItems: string;
	aggregateProprietaryItems: string;
	ogmAiHistory: string;
	locationLevelPricing: string;
	ogmAiOptimizations: string;
	bulkApply: string;
	ogmAiGroupings: string;
	ogmAiCombined: string;
	offOrderGuideSavings: string;
	massCustomerItemNumberImport: string;
	CustomerItemNumberMappings: string;
}

export interface ICustomerReportTableMessages {
	noReportsAvailable: string;
	noReportsAvailableForCustomer: string;
	customerHeader: string;
	reportNameHeader: string;
	reportDateHeader: string;
	reportRowCountHeader: string;
	actionsHeader: string;
	downloadLink: string;
	reportNames: ICustomerReportNames;
}

// CONTAINERS

export interface ISearchContainerMessages {
	header: string;
	searchButton: string;
	addProductsBy: string;
	addByProductNumber: string;
	noMatchesFound: string;
	emptySearchMessage: string;
	prioritizeButton: string;
	prioritizeButtonReadOnly: string;
	distributionCenterPillLabel: string;
	multipleDistributionCenterPillValue: string;
	multiplePackSizePillValue: string;
}

export interface IAdminContainerMessages {
	newUser: INewUserMessages;
	editUser: IEditUserMessages;
	users: IUsersMessages;
}

export interface IUsersMessages {
	deleteUserModalError: string;
	deleteUserModalBody: string;
	deleteUserModalTitle: string;
	changePasswordToastTitle: string;
	changePasswordToastBody: string;
	deleteUserToastTitle: string;
	deleteUserToastBody: string;
	assignmentStatus: string;
	assigned: string;
	unassigned: string;
	customerFilter: string;
}

export interface INewUserMessages {
	formHeader: string;
	formAccountHeader: string;
	formRolesHeader: string;
	formUserProfileHeader: string;
	formFirstNamePlaceholder: string;
	formLastNamePlaceholder: string;
	formEmailPlaceholder: string;
	formPasswordPlaceholder: string;
	formPasswordValidationMessage: string;
	formSaveButton: string;
}

export interface IEditUserMessages {
	firstNameValidationWarning: string;
	lastNameValidationWarning: string;
}

export interface IBlueprintManagementMessages {
	newBlueprint: INewBlueprintMessages;
	deleteBlueprint: IDeleteBlueprintMessages;
	editBlueprint: IEditBlueprintMessages;
	massImport: IMassImportMessages;
}

export interface IActivityMessages {
	productAdd: string;
	productRemove: string;
	blueprintDelete: string;
	unknown: string;
}

export interface IAlertsMessages {
	actions: string;
	coverageAlerts: string;
	discontinuedAlerts: string;
	distributionCenterCoverage: string;
	distributionCenterHeader: string;
	locationCoverage: string;
	masterItem: string;
	masterOrderGuide: string;
	noAlertsRequiringAttention: string;
	severity: string;
	output: IAlertsOutput;
	acknowledgeAlertToastMessage: string;
	acknowledgeAlertToastTitle: string;
}

export interface IAlertsOutput {
	discontinued: string;
	other: string;
}

export interface IOrderGuideDownloadMessages {
	requestModal: IRequestOrderGuideDownloadModalMessages;
}

export interface IRequestOrderGuideDownloadModalMessages {
	filterPlaceholder: string;
	header: string;
	subHeader: string;
	submitButton: string;
	filterLabels: IOGDownlaodFilterLabels;
	dcMessage: string;
	filterLocationPlaceholder: string;
}

export interface IOGDownlaodFilterLabels {
	distributionCenter: string;
}

export interface IStockMatrixMessages {
	requestModal: IRequestStockMatrixModalMessages;
}

export interface IRequestStockMatrixModalMessages {
	filterPlaceholder: string;
	header: string;
	subHeader: string;
	submitButton: string;
	filterLabels: IStockMatrixFilterLabels;
	filterLocationPlaceholder: string;
}

export interface IStockMatrixFilterLabels {
	distributionCenter: string;
}

export interface IMassCustomerItemNumberImportMessages {
	toasts: IReportRequestToasts;
}

export interface ICustomerItemNumberMappingsMessages {
	toasts: IReportRequestToasts;
}

export interface INewBlueprintMessages {
	formHeader: string;
	formMogNamePlaceholder: string;
	formMogNameHeader: string;
	formMogSupplierHeader: string;
	formMogNameErrorValidation: string;
	successTitle: string;
	successBody: string;
}

export interface IDeleteBlueprintMessages {
	successTitle: string;
	successBody: string;
	failureTitle: string;
	failureBody: string;
}

export interface IEditBlueprintMessages {
	formHeader: string;
	formMogNamePlaceholder: string;
	formMogNameHeader: string;
	formMogNameErrorValidation: string;
	successTitle: string;
	successBody: string;
}

export interface IMassImportMessages {
	downloadTemplate: string;
	formHeader: string;
	formFileInputHeader: string;
	formFileInputErrorValidation: string;
	formSupplierHeader: string;
	failureTitle: string;
	failureBody: string;
	successTitle: string;
	successBody: string;
	upload: string;
	massImportInProgressForCustomer: string;
	bulkApply: string;
	previewChanges: string;
	applyChanges: string;
	acknowledgement: string;
	confirmChanges: string;
}

export interface ILoginContainerMessages {
	signInModule: ISignInModuleMessages;
}

export interface ISingleSignOnMessages {
	sessionExpired: string;
}

export interface IBrowserUnsupportedContainerMessages {
	header: string;
	message: string;
	resolveHeader: string;
	helpHeader: string;
	helpListItemZero: string;
	helpListItemOne: string;
}

export interface ICookiesDisabledMessages {
	cookiesDisabledHeader: string;
	cookiesDisabledBody: string;
	cookiesDisabledListHeader: string;
	cookiesDisabledIELabel: string;
	cookiesDisabledChromeLabel: string;
	cookiesDisabledFirefoxLabel: string;
}

export interface ISignInModuleMessages {
	cookiesDisabled: ICookiesDisabledMessages;
	signInError: string;
	unknownError: string;
	username: string;
	password: string;
}

export interface IHomeMessages {
	newMasterOrderGuide: string;
	allSuppliers: string;
	allLocations: string;
	orderGuideSearchPlaceholder: string;
	blueprintCard: IBlueprintCardMessages;
	deleteMogWarning: string;
}

export interface ILocationAssignmentMessages {
	reviewPrompt: string;
	removalNotice: string;
	additionNotice: string;
	save: string;
	saveToastMessageTitle: string;
	saveToastMessageBody: string;
	locationSelectionTable: ILocationSelectionTable;
}

export interface ILocationSelectionTable {
	name: string;
	id: string;
	addressLine1: string;
	addressLine2: string;
	city: string;
	state: string;
	zip: string;
	distributionCenterName: string;
	distributionCenterNameTitle: string;
	distributionCenterExternalCode: string;
	distributionCenterExternalCodeTitle: string;
}

export interface IDistributionCenterAssignmentMessages {
	reviewPrompt: string;
	removalNotice: string;
	additionNotice: string;
	save: string;
	saveToastMessageTitle: string;
	saveToastMessageBody: string;
	distributionCenterSelectionTable: IDistributionCenterSelectionTable;
}

export interface IDistributionCenterSelectionTable {
	name: string;
	nameTitle: string;
	externalCode: string;
	externalCodeTitle: string;
}

export interface IRestrictionsWorkflowMessages {
	selectSupplier: string;
	header: string;
	modalHeader: string;
	modalQuestion: string;
	modalStrongWarning: string;
	modalAdditionalInfo: string;
	restrictProducts: string;
	productNumberHeader: string;
	enterProductNumbers: string;
	numberOfRestrictions: string;
	enterDefaultRestrictionMessage: string;
	defaultRestrictionMessage: string;
	validation: IRestrictionsWorkflowValidationMessages;
}

export interface IRestrictionsWorkflowValidationMessages {
	productNumberValidationLegend: string;
	validProductNumber: string;
	invalidProductNumber: string;
	productOnCategoryRestrictionList: string;
}

export interface IRestrictionLocationAssignmentMessages {
	Title: string;
}

export interface IMetaProductWorkflowMessages {
	actions: string;
	addBySupc: IAddBySupcMessages;
	branding: string;
	categories: string;
	coverage: string;
	delete: string;
	demand: string;
	displaySpecItem: string;
	earlyOrder: string;
	distributionCenterCoverage: string;
	distributionCenterCoverageGrid: IDistributionCenterCoverageGridViewMessages;
	nonStock: string;
	prioritiyNumber: string;
	prioritizeMetaProductItems: IPrioritizeMetaProductItemsMessages;
	rearrange: IRearrangeMessages;
	remote: string;
	searchForProducts: string;
	searching: string;
	productTable: IProductTable;
	addBySearch: string;
	priceLoadingFailedWarning: string;
	pendingRebuild: string;
	perms: string;
	permsTitle: string;
	specIndicator: string;
	rulesets: ISelectionRuleSetMessages;
	aiSettings: IAiSettingMessages;
	isSelectByUm: IIsSelectByUmMessages;
	distributionCenterFilterPlaceholder: string;
	distributionCenterFilterValue: string;
	selectionTab: string;
	coverageTab: string;
}

export interface IProductTable {
	productNumber: string;
	description: string;
	brand: string;
	packSize: string;
	um: string;
	price: string;
	pricePerUnit: string;
	proprietary: string;
	stocking: string;
	coverage: string;
	priority: string;
	actions: string;
	statusActive: string;
	statusInactive: string;
	statusDiscontinued: string;
	eligible: string;
	ineligible: string;
	some: string;
	manfNumber: string;
	distributionCenter: string;
	code: string;
	status: string;
	eligibility: string;
	productAlert: string;
	specIndicator: string;
}

export interface IDistributionCenterCoverageGridViewMessages {
	coverageView: string;
	priceView: string;
	pricePerUnitView: string;
}

export interface IPrioritizeMetaProductItemsMessages {
	updateMessageTitle: string;
	updateMessageBody: string;
	addMessageTitle: string;
	addMessageBody: string;
	addMasterItem: string;
	saveMasterItem: string;
	enterMasterItemName: string;
	prioritizeTable: IPrioritizeTableMessages;
	recommendationsTitle: string;
	categoryFilterTitle: string;
	acceptStagedGrouping: string;
	stagedNewGrouping: string;
}

export interface IPrioritizeTableMessages {
	collapse: string;
	expand: string;
}

export interface ISelectionRuleSetMessages {
	prioritySDRN: string;
	prioritySDR: string;
	prioritySD: string;
	leadTimeSDRN: string;
	USFLeadTime: string;
	leadTimeSDR: string;
	leadTimeSD: string;
	onlyStocked: string;
	onlyRemote: string;
	unknown: string;
}

export interface IAiSettingMessages {
	auto: string;
	manual: string;
	autoReprioritize: string;
	off: string;
	unknown: string;
}

export interface IIsSelectByUmMessages {
	enabled: string;
	disabled: string;
	warningModal: {
		header: string;
		message: string;
		dynamicMessage: string;
		previewChanges: string;
	};
}

export interface IAddBySupcMessages {
	addProducts: string;
	addProductsBy: string;
	addProductsToNewMasterItem: string;
	addProductsToExistingMasterItem: string;
}

export interface IRearrangeMessages {
	unsavedChanges: string;
	metaProductTable: IMetaProductTableMessages;
	validationErrors: IRearrangeValidationErrors;
	panes: IPanesMessages;
	distributionCenterOverrides: string;
}

export interface IRearrangeValidationErrors {
	title: string;
	metaProductName: string;
	metaProductItems: string;
}

export interface IMetaProductTableMessages {
	noProducts: string;
	productNumber: string;
	description: string;
	packSize: string;
	um: string;
	coverage: string;
}

export interface IPanesMessages {
	masterItemDeleteWarning: string;
	moveSelected: string;
	moveAll: string;
	addGrouping: string;
	undoChanges: string;
	newMasterItem: string;
	aiRegrouping: string;
	copiedMasterItem: string;
	copyName: string;
}

export interface IBlueprintCardMessages {
	created: string;
	locations: string;
	delete: string;
	rename: string;
	items: string;
}

export interface ICustomersMessages {
	customerList: ICustomerList;
}

export interface ICustomerList {
	header: string;
	searchPlaceholder: string;
	noCustomersMessage: string;
}

// COMPONENTS

export interface IAddBySupcModalMessages {
	enterProductNumbers: string;
}

export interface INavigationBarMessages {
	masterOrderGuides: string;
	orderGuideDownload: string;
	restrictions: string;
	manageUsers: string;
	alerts: string;
	signOut: string;
	changeCustomer: string;
	reports: string;
}

export interface IProprietary {
	allProprietaryLabel: string;
	allProprietaryTitle: string;
	someProprietaryLabel: string;
	someProprietaryTitle: string;
}

export interface IBranding {
	isBranded: string;
	isBrandedDetail: string;
}

export interface IUserProfileCardMessages {
	accountHeader: string;
	rolesHeader: string;
	userProfileHeader: string;
	firstNamePlaceHolder: string;
	lastNamePlaceHolder: string;
	saveButton: string;
}

export interface IProductDetailsSlideoutMessages {
	productNumber: string;
	description: string;
	brand: string;
	details: string;
	foodNutrition: string;
	packSize: string;
	gtin: string;
	catchweight: string;
	locationLevelPricing: string;
}

export interface INutritionLabelMessages {
	servingSize: string;
	nutritionFacts: string;
	amountPerServing: string;
	calories: string;
	caloriesFromFat: string;
	percentDailyValue: string;
	percentDailyValueTop: string;
	totalFat: string;
	saturatedFat: string;
	transFat: string;
	monounsaturatedFat: string;
	cholesterol: string;
	sodium: string;
	totalCarbs: string;
	dietaryFiber: string;
	sugars: string;
	protein: string;
	vitaminD: string;
	calcium: string;
	iron: string;
	potassium: string;
	dailyValueDescription: string;
	ingredients: string;
}

export interface ISpecIndicatorMessages {
	hard: string;
	required: string;
	open: string;
	unassigned: string;
	edit: string;
	spec: string;
}

export interface ISuggestionDetailMessages {
	dismissStaleOptimization: string;
}

export interface IRebatePill {
	rebate: string;
	contract: string;
	rebateAvailable: string;
}

export interface IPriceAttributes {
	rebate: string;
	contract: string;
	audited: string;
	rebateAvailable: string;
}

// HELPERS

/**
 * Converts a path selector into a string
 * @example
 * o => o.my.object.key.path
 * returns
 * "my.object.key.path"
 * @param val
 */
export function key(val: (obj: IMessages) => any) {
	const strVal = String(val);
	const regex = /((?:\w+\.)+\w+)/g;
	const match = regex.exec(strVal);
	const fields = match[1].split('.');
	return fields.slice(1, fields.length).join('.');
}

/** Formats a message given a messagepath. */
export function formatMessage(
	intl: intlShape,
	selector: (obj: IMessages) => string,
	values?: object
): string {
	return intl.formatMessage({ id: key(selector) }, values);
}
